import { Fragment, type ReactNode } from "react"
import type { StoreRichTextDocument } from "../../types"
import styles from "./product-description.module.css"

type RichNode = {
  type?: string
  text?: string
  marks?: Array<{ type?: string; attrs?: Record<string, unknown> }>
  content?: RichNode[]
}

const PawIcon = () => (
  <span className={styles.listIcon} data-testid="product-description-paw" data-vetpets-li-icon="paw-print" aria-hidden="true">
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="4" r="2" />
      <circle cx="18" cy="8" r="2" />
      <circle cx="20" cy="16" r="2" />
      <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-7 0V15a5 5 0 0 1 2-4Z" />
      <circle cx="4" cy="8" r="2" />
      <circle cx="2" cy="16" r="2" />
    </svg>
  </span>
)

const renderText = (node: RichNode, key: string): ReactNode => {
  let value: ReactNode = node.text ?? ""
  for (const [index, mark] of (node.marks ?? []).entries()) {
    const markKey = `${key}-mark-${index}`
    if (mark.type === "bold") value = <strong key={markKey}>{value}</strong>
    else if (mark.type === "italic") value = <em key={markKey}>{value}</em>
    else if (mark.type === "strike") value = <s key={markKey}>{value}</s>
  }
  return <Fragment key={key}>{value}</Fragment>
}

const renderNodes = (
  nodes: RichNode[] | undefined,
  prefix: string,
  isRoot = false
): ReactNode =>
  nodes?.map((node, index) => {
    const key = `${prefix}-${index}`
    if (node.type === "text") return renderText(node, key)
    if (node.type === "hardBreak") return <br key={key} />
    const children = renderNodes(node.content, key)
    switch (node.type) {
      case "paragraph": {
        const isIntro = isRoot && index === 0
        return (
          <p
            key={key}
            className={isIntro ? styles.intro : undefined}
            data-testid={isIntro ? "product-description-intro" : undefined}
          >
            {children}
          </p>
        )
      }
      case "bulletList": return <ul key={key}>{children}</ul>
      case "orderedList": return <ol key={key}>{children}</ol>
      case "listItem": return <li key={key}><PawIcon />{children}</li>
      default: return null
    }
  }) ?? null

export default function ProductDescription({ document }: { document?: StoreRichTextDocument | null }) {
  if (!document?.content) return null
  return (
    <div className={styles.productDescription}>
      {renderNodes(document.content as RichNode[], "description", true)}
    </div>
  )
}
